/** The one-line confirmation of an immediate action (plan §3: "confirm where
 *  guest-visible or remote") — the popover on the control that started it (the Actions
 *  button, a section's button), never a modal. Confirm → running → the result, or why
 *  nothing happened, in the operator's terms. `Enter` confirms (the primary is focused),
 *  `Esc` backs out; it cannot close while the action runs.
 *
 *  `onDone` instead of `renderResult` closes it on success (the result shows where the
 *  action belongs, e.g. a revealed link in place of its masked form). */
import { Alert, Box, Button, LinearProgress, Popover, Typography } from '@mui/material';
import { useId, useState, type ReactNode } from 'react';

import { BookingActionError, type BookingActionErrorCode } from '@/api/bookingActions';
import { Kbd } from '@/features/records/page/Kbd';

const PAPER_SX = { p: 1.5, mt: 0.5, width: 360, maxWidth: 'calc(100vw - 32px)', display: 'grid', gap: 1 } as const;

const REFUSALS: Partial<Record<BookingActionErrorCode, string>> = {
  disabled: 'It is switched off on this environment.',
  no_guest: 'The booking has no guest.',
  not_publishable: 'The guest does not see this booking (cancelled, on hold, …).',
  no_invite_link: 'The booking has no invitation link yet — the next import with notifications creates it.',
  no_departure: 'The booking has no start time, so there is no check-in list.',
  link_unavailable: 'The booking has no such link.',
};

/** Why an immediate action did nothing: the console's words for a known code, else the
 *  server's. */
export function refusalText(e: unknown): string {
  if (e instanceof BookingActionError) return REFUSALS[e.action.code] ?? e.action.detail;
  return e instanceof Error && e.message ? e.message : 'The request failed.';
}

type Phase = 'confirm' | 'running' | 'done' | 'failed';

export function ImmediateActionPopover<R>({
  anchorEl,
  onClose,
  title,
  children,
  confirmLabel,
  confirmDisabled = false,
  run,
  renderResult,
  onDone,
  dataKey,
}: {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  title: string;
  /** What the action does — the confirmation's body. */
  children: ReactNode;
  confirmLabel: string;
  /** E.g. while the facts it confirms are still loading. */
  confirmDisabled?: boolean;
  run: () => Promise<R>;
  renderResult?: (result: R) => ReactNode;
  onDone?: (result: R) => void;
  /** `data-immediate` on the paper (tests, the harness). */
  dataKey?: string;
}) {
  const titleId = useId();
  const [phase, setPhase] = useState<Phase>('confirm');
  const [result, setResult] = useState<R | null>(null);
  const [error, setError] = useState<unknown>(null);

  const confirm = () => {
    setPhase('running');
    run().then(
      (r) => {
        if (onDone) {
          onDone(r);
          return;
        }
        setResult(r);
        setPhase('done');
      },
      (e: unknown) => {
        setError(e);
        setPhase('failed');
      },
    );
  };
  const running = phase === 'running';

  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={() => {
        if (!running) onClose();
      }}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      slotProps={{
        paper: {
          role: phase === 'done' ? 'dialog' : 'alertdialog',
          'aria-labelledby': titleId,
          'aria-busy': running || undefined,
          'data-immediate': dataKey,
          'data-phase': phase,
          sx: PAPER_SX,
        } as object,
      }}
    >
      <Typography id={titleId} variant="body2" sx={{ fontWeight: 600 }}>
        {title}
      </Typography>
      {(phase === 'confirm' || running) && (
        <>
          {children}
          {running && <LinearProgress aria-label={`${title}: running`} sx={{ height: 2 }} />}
          <Box sx={{ display: 'flex', gap: 0.75, justifyContent: 'flex-end' }}>
            <Button size="small" onClick={onClose} disabled={running}>
              Back
            </Button>
            <Button
              size="small"
              variant="contained"
              autoFocus
              onClick={confirm}
              disabled={running || confirmDisabled}
              data-confirm-immediate
            >
              {confirmLabel}
              {!running && <Kbd>↵</Kbd>}
            </Button>
          </Box>
        </>
      )}
      {phase === 'failed' && (
        <>
          <Alert severity="warning" sx={{ py: 0, '& .MuiAlert-message': { typography: 'body2', py: 0.75 } }}>
            Nothing was done: {refusalText(error)}
          </Alert>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button size="small" onClick={onClose} autoFocus>
              Close
            </Button>
          </Box>
        </>
      )}
      {phase === 'done' && result !== null && (
        <>
          {renderResult?.(result)}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button size="small" onClick={onClose} autoFocus>
              Close
            </Button>
          </Box>
        </>
      )}
    </Popover>
  );
}
